/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import {
	Container,
	FlexLayout,
	FlexItem,
	SVGIcon,
	Button,
	classnames as cx,
	Flyout,
} from '@waca/ca-ui-toolkit';
import chevronRight16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-right_16.js';
import stringResource from 'ba-filter-nls';
import PredicateSelector from './PredicateSelector';
import FilterSummary from './FilterSummary';

import './ConditionBuilder.scss';

export default class ConditionBuilder extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		onChange: PropTypes.func.isRequired,
		toggleSlideout: PropTypes.func.isRequired,
		open: PropTypes.bool.isRequired,
		showToggleButton: PropTypes.bool,
		condition: PropTypes.object,
		inverted: PropTypes.bool,
		height: PropTypes.number,
		readOnly: PropTypes.bool,
		hasManualSelections: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		toggleSlideout: null,
		open: false,
		showToggleButton: true,
		height: 40,
		readOnly: false,
		inverted: false,
		hasManualSelections: false,
	};

	_filterSummary: FilterSummary;
	_addConditionButton: Button;

	// Event handlers
	_setFocus = (showToggleButton, condition) => {
		if (showToggleButton) {
			if (condition) {
				if (this._filterSummary) this._filterSummary.focus();
			} else if (this._addConditionButton) this._addConditionButton.focus();
		}
	};
	// Condition builder has opened/closed
	_onToggle = () => {
		const { open, condition, toggleSlideout, showToggleButton, readOnly } = this.props;

		if (readOnly) {
			return;
		}
		toggleSlideout(!open);

		// We are closing the menu so focus the 'Add condition' button
		open && this._setFocus(showToggleButton, condition);
	};

	// The condition has changed
	_onConditionValueChanged = result => {
		this.props.onChange(result);
	};

	_renderCondition = () => {
		const {
			open,
			condition,
			showToggleButton,
			height,
			readOnly,
			inverted,
			hasManualSelections,
		} = this.props;

		return (
			<div>
				{condition === null ? (
					showToggleButton && (
						<FlexLayout
							className={!condition && cx('bi-filter-condition-builder', { open })}
							alignItems="center"
							direction="row"
							height={height}
						>
							<FlexItem
								className="bi-filter-condition-builder-container"
								grow
								margin={2}
							>
								<FlexLayout
									className="bi-filter-condition-builder-button"
									alignItems="center"
									direction="row"
								>
									<FlexItem grow onClick={this._onToggle} textAlign="left">
										{stringResource.get('addFilterCondition')}
									</FlexItem>
									<FlexItem>
										<Button
											ref={btn => {
												this._addConditionButton = btn;
											}}
											data-tid="addFilterClickable"
											onClick={this._onToggle}
											intent="primary"
											variant="inline"
											icon={
												<SVGIcon
													pad
													iconId={chevronRight16.id}
													size="small"
													rotate={open ? 180 : 0}
												/>
											}
											disabled={readOnly}
											title={stringResource.get('addFilterCondition')}
											aria-pressed={open}
										/>
									</FlexItem>
								</FlexLayout>
							</FlexItem>
						</FlexLayout>
					)
				) : (
					<FlexLayout>
						<FlexItem grow>
							<FilterSummary
								ref={summary => {
									this._filterSummary = summary;
								}}
								title={stringResource.get('filterCondition')}
								condition={condition}
								onMenu={this._onToggle}
								menuButtonPressed={open}
								inverted={inverted}
								readOnly={readOnly}
								hasManualSelections={hasManualSelections}
							/>
						</FlexItem>
					</FlexLayout>
				)}
			</div>
		);
	};

	render() {
		const { open, condition } = this.props;

		return (
			<FlexLayout grow direction="row">
				<FlexItem grow margin={!condition ? [0, 0, 2, 0] : [0, 0, 3, 0]}>
					{this._renderCondition()}
					{open && (
						<Flyout placement="right" theme="dark">
							<Container gutter={3}>
								<PredicateSelector
									condition={condition}
									onConditionChange={this._onConditionValueChanged}
									onClose={this._onToggle}
									open={open}
								/>
							</Container>
						</Flyout>
					)}
				</FlexItem>
			</FlexLayout>
		);
	}
}
